import { useSuspenseQuery } from '@apollo/client';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import { useCompleteFragment } from '@eluve/apollo-client';
import { TooltipLabel } from '@eluve/blocks';
import { ColDefBuilder, DataTable, Icon, IconProps } from '@eluve/components';
import {
  AppointmentFileTypesEnum,
  UserUploadStatusEnum,
} from '@eluve/graphql-types';

import { AppointmentFileActionsColumn } from './AppointmentFileActionsColumn';
import { FileIdsPopover } from './FileIdsPopover';
import { FileTagsDialog } from './FileTagsDialog';
import { appointmentFilesFragment, appointmentFilesQuery } from './operations';

type Row = {
  tenantId: string;
  appointmentId: string;
  fileId: string;
  fileUrl: string | null;
  fileName: string | undefined;
  isFinalChunk: boolean;
  status: UserUploadStatusEnum | undefined;
  type: AppointmentFileTypesEnum;
  transcriptionStatus?: string;
  segmentId?: string;
  segmentStartedAt?: string;
  fileUploadedAt?: string;
  tags: {
    tagName: string;
    metadata: any;
    createdAt: string;
    isSystem: boolean;
  }[];
};

const columns = new ColDefBuilder<Row>()
  .colDef({
    header: 'Actions',
    cell: ({ row: { original } }) => (
      <AppointmentFileActionsColumn
        tenantId={original.tenantId}
        appointmentId={original.appointmentId}
        fileId={original.fileId}
        filePath={original.fileUrl ?? ''}
        fileType={original.type}
        fileStatus={original.status}
      />
    ),
  })
  .colDef({
    id: 'ids',
    header: 'IDs',
    cell: ({ row }) => (
      <FileIdsPopover
        ids={[
          { label: 'File ID', value: row.original.fileId },
          { label: 'GCS Path', value: row.original.fileUrl },
          { label: 'Segment ID', value: row.original.segmentId },
        ]}
      />
    ),
  })
  .colDef({
    id: 'tags',
    accessorFn: (row) => row.tags.length,
    header: 'Tags',
    cell: ({ row }) => <FileTagsDialog tags={row.original.tags} />,
  })
  .defaultSortable('type', {
    cellRenderer: (row) => {
      const iconName = match<AppointmentFileTypesEnum, IconProps['name']>(
        row.type,
      )
        .with('WEBSOCKET_AUDIO', () => 'RadioTower')
        .with('CHUNKED_AUDIO', () => 'Puzzle')
        .with('USER_BACKUP_AUDIO', () => 'AudioWaveform')
        .with('RAW_TRANSCRIPT_METADATA', () => 'FileBracesCorner')
        .otherwise(() => 'Files');

      return (
        <TooltipLabel label={row.type}>
          <div>
            <Icon name={iconName} />
          </div>
        </TooltipLabel>
      );
    },
  })
  .defaultSortable('status')
  .defaultBoolean('isFinalChunk', 'Is Final')
  .dateSortable('fileUploadedAt', 'File Uploaded At')
  .defaultSortable('transcriptionStatus', 'Transcription Status')
  .dateSortable('segmentStartedAt', 'Segment Started At')
  .defaultSortable('fileName', 'File Name')
  .build();

export const AdminAppointmentFiles: React.FC = () => {
  const { tenantId, appointmentId } = useParams() as {
    tenantId: string;
    appointmentId: string;
  };

  useSuspenseQuery(appointmentFilesQuery, {
    variables: { tenantId, appointmentId },
  });

  const { appointmentFiles } = useCompleteFragment({
    fragment: appointmentFilesFragment,
    key: { id: appointmentId },
  });
  const rows = useMemo(() => {
    return appointmentFiles.map<Row>((af) => {
      return {
        tenantId,
        appointmentId,
        fileId: af.fileId,
        fileUrl: af.userUpload?.fileUrl,
        fileName: af.userUpload?.originalFileName ?? undefined,
        type: af.fileType,
        isFinalChunk: af.userUpload?.transcript?.isFinalChunk ?? false,
        status: af.userUpload?.status,
        transcriptionStatus:
          af.fileType === 'RAW_TRANSCRIPT_METADATA'
            ? 'N/A'
            : af.userUpload?.transcript?.transcriptionStatus ?? 'NO TRANSCRIPT',
        segmentId: af.userUpload?.transcript?.segmentId,
        segmentStartedAt:
          af.userUpload?.transcript?.appointment_segment?.recordingStartedAt,
        fileUploadedAt: af.userUpload?.uploadedAt ?? undefined,
        tags: (af.tags ?? []).map((t) => {
          return {
            tagName: t.tag.tag,
            createdAt: t.createdAt,
            metadata: t.metadata,
            isSystem: t.tag.isSystemTag ?? false,
          };
        }),
      };
    });
  }, [appointmentFiles, tenantId, appointmentId]);

  return (
    <div>
      <DataTable
        data={rows}
        columns={columns}
        initialSortingState={[
          {
            id: 'fileName',
            desc: true,
          },
        ]}
      />
    </div>
  );
};
